import { useCallback, useRef } from "react";

import { useLatestRef, useOnUnmountEffect } from "./react-helpers";

/**
 * drag: the user is actively dragging the element around
 * save: the user has finished dragging the element and we should save the new position
 * cancel: the user has cancelled the drag operation
 */
export type DragAction = "drag" | "save" | "cancel";

interface DragMonitorCallbackParams<Context, Action extends DragAction> {
  context: Context;
  delta: { x: number; y: number };
  action: Action;
  event?: PointerEvent;
}

interface DragMonitorParams<Context> {
  cleanup?: React.MutableRefObject<(() => void) | undefined>;

  fireOnMoveOnPointerDown?: boolean;

  // these params get latest so don't worry about callback/deps
  onStart: (e: React.PointerEvent<HTMLDivElement>) => {
    shouldStart: boolean;
    context: Context;
    shouldCaptureWhenNotStarting?: boolean;
  };
  onMove: (params: DragMonitorCallbackParams<Context, "drag">) => { x: number; y: number } | void;
  onDone: (params: DragMonitorCallbackParams<Context, "save" | "cancel">) => void;
}

export function useDragOnPointerDown<Context>(
  params: Omit<DragMonitorParams<Context>, "cleanupRef">
) {
  const cleanup = useRef<() => void>();
  const onDoneRef = useLatestRef(params.onDone);
  const onMoveRef = useLatestRef(params.onMove);
  const onStartRef = useLatestRef(params.onStart);

  useOnUnmountEffect(() => {
    cleanup.current?.();
  });

  return useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      startMonitoringDrag(e, {
        cleanup,
        onDone: onDoneRef.current,
        onMove: onMoveRef.current,
        onStart: onStartRef.current,
        fireOnMoveOnPointerDown: params.fireOnMoveOnPointerDown,
      });
    },
    [onDoneRef, onMoveRef, onStartRef, params.fireOnMoveOnPointerDown]
  );
}

export function startMonitoringDrag<Context>(
  e: React.PointerEvent<HTMLDivElement>,
  params: DragMonitorParams<Context>
) {
  const { shouldStart, context, shouldCaptureWhenNotStarting } = params.onStart(e);
  if (e.button !== 0) {
    return null;
  }

  if (!shouldStart) {
    if (shouldCaptureWhenNotStarting) {
      e.currentTarget.setPointerCapture(e.pointerId);
      e.stopPropagation();
      e.preventDefault();
    }
    return null;
  }

  e.currentTarget.setPointerCapture(e.pointerId);
  e.stopPropagation();
  e.preventDefault();

  const originalTarget = e.currentTarget;
  const startClientX = e.clientX;
  const startClientY = e.clientY;
  let delta = { x: 0, y: 0 };

  if (params.fireOnMoveOnPointerDown) {
    delta = params.onMove({ delta, action: "drag", context }) ?? delta;
  }

  const cleanup = () => {
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointercancel", onPointerCancel);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("keydown", onKeyDown);

    if (params.cleanup) {
      params.cleanup.current = undefined;
    }
  };

  if (params.cleanup) {
    params.cleanup.current = cleanup;
  }

  const onPointerMove = (e: PointerEvent) => {
    delta = { x: e.clientX - startClientX, y: e.clientY - startClientY };
    delta = params.onMove({ delta, action: "drag", event: e, context }) ?? delta;
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      params.onDone({ delta, action: "cancel", context });
      cleanup();
    }
  };

  const makeOnDone = (mode: Extract<DragAction, "save" | "cancel">) => {
    return (e: PointerEvent) => {
      originalTarget.releasePointerCapture(e.pointerId);
      params.onDone({ delta, action: mode, event: e, context });

      cleanup();
    };
  };

  const onPointerCancel = makeOnDone("cancel");
  const onPointerUp = makeOnDone("save");

  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointercancel", onPointerCancel);
  document.addEventListener("pointerup", onPointerUp, { once: true });
  document.addEventListener("keydown", onKeyDown);
}
